import { Listbox } from '@headlessui/react';

import useLocale from 'next-translate/useTranslation';
import { useEffect, useRef, useState } from 'react';

import clsxm from '@/lib/clsxm';

import { SimpleTooltip } from '@/components/ui/tooltip';

const themes = [
  { name: 'Light', value: 'light', icon: LightIcon },
  { name: 'Dark', value: 'dark', icon: DarkIcon },
  { name: 'System', value: 'system', icon: SystemIcon },
] as const;

type ThemeOption = (typeof themes)[number];

const SYSTEM_THEME = themes[2];

function LightIcon(props: any) {
  return (
    <svg aria-hidden='true' viewBox='0 0 16 16' {...props}>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M7 1a1 1 0 0 1 2 0v1a1 1 0 1 1-2 0V1Zm4 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm2.657-5.657a1 1 0 0 0-1.414 0l-.707.707a1 1 0 0 0 1.414 1.414l.707-.707a1 1 0 0 0 0-1.414Zm-1.415 11.313-.707-.707a1 1 0 0 1 1.415-1.415l.707.708a1 1 0 0 1-1.415 1.414ZM16 7.999a1 1 0 0 0-1-1h-1a1 1 0 1 0 0 2h1a1 1 0 0 0 1-1ZM7 14a1 1 0 1 1 2 0v1a1 1 0 1 1-2 0v-1Zm-2.536-2.464a1 1 0 0 0-1.414 0l-.707.707a1 1 0 0 0 1.414 1.414l.707-.707a1 1 0 0 0 0-1.414Zm0-8.486A1 1 0 0 1 3.05 4.464l-.707-.707a1 1 0 0 1 1.414-1.414l.707.707ZM3 8a1 1 0 0 0-1-1H1a1 1 0 0 0 0 2h1a1 1 0 0 0 1-1Z'
      />
    </svg>
  );
}

function DarkIcon(props: any) {
  return (
    <svg aria-hidden='true' viewBox='0 0 16 16' {...props}>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M7.23 3.333C7.757 2.905 7.68 2 7 2a6 6 0 1 0 0 12c.68 0 .758-.905.23-1.332A5.989 5.989 0 0 1 5 8c0-1.885.87-3.568 2.23-4.668ZM12 5a1 1 0 0 1 1 1 1 1 0 0 0 1 1 1 1 0 1 1 0 2 1 1 0 0 0-1 1 1 1 0 1 1-2 0 1 1 0 0 0-1-1 1 1 0 1 1 0-2 1 1 0 0 0 1-1 1 1 0 0 1 1-1Z'
      />
    </svg>
  );
}

function SystemIcon(props: any) {
  return (
    <svg aria-hidden='true' viewBox='0 0 16 16' {...props}>
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M1 4a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v4a3 3 0 0 1-3 3h-1.5l.31 1.242c.084.333.36.573.63.808.091.08.182.158.264.24A1 1 0 0 1 11 15H5a1 1 0 0 1-.704-1.71c.082-.082.173-.16.264-.24.27-.235.546-.475.63-.808L5.5 11H4a3 3 0 0 1-3-3V4Zm3-1a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H4Z'
      />
    </svg>
  );
}

interface ThemeSelectorProps {
  variant?: 'default' | 'light';
  align?: 'left' | 'right' | 'center';
  className?: string;
}

export function ThemeSelector({
  variant = 'default',
  align = 'center',
  className,
  ...props
}: ThemeSelectorProps) {
  const { t } = useLocale('common');

  const [selectedTheme, setSelectedTheme] = useState<ThemeOption>(SYSTEM_THEME);
  // Skip the very first DOM write so we don't clobber the value the inline
  // script in _document.tsx already set from localStorage. The mount-sync
  // effect below captures the same initial render as this write effect, so
  // without this guard we'd transiently write data-theme="system" before
  // the sync's setState commits, triggering the MutationObserver to
  // overwrite localStorage.
  const skipNextWriteRef = useRef(true);

  // Sync from DOM once on mount — the inline script in _document.tsx has
  // already written the actual theme to documentElement. Reading it here
  // (rather than in the initial state) avoids hydration mismatches.
  useEffect(() => {
    const fromDom = document.documentElement.getAttribute('data-theme');
    const found = themes.find((theme) => theme.value === fromDom);
    if (found && found.value !== selectedTheme.value) {
      setSelectedTheme(found);
    }
    // intentionally run only once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (skipNextWriteRef.current) {
      skipNextWriteRef.current = false;
      return;
    }
    document.documentElement.setAttribute('data-theme', selectedTheme.value);
  }, [selectedTheme.value]);

  const buttonClasses =
    variant === 'light'
      ? 'flex h-6 w-6 items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm ring-1 ring-white/20 hover:bg-white/20'
      : 'flex h-6 w-6 items-center justify-center rounded-lg shadow-md shadow-black/5 ring-1 ring-black/5 dark:bg-slate-700 dark:ring-inset dark:ring-white/5';

  const iconFillClass = variant === 'light' ? 'fill-white' : 'fill-slate-400';

  return (
    <Listbox
      as='div'
      value={selectedTheme}
      onChange={setSelectedTheme}
      className={className}
      {...props}
    >
      <Listbox.Label className='sr-only'>{t('Theme')}</Listbox.Label>
      <SimpleTooltip title={t('Theme')} side='bottom'>
        <Listbox.Button
          className={buttonClasses}
          aria-label={t(selectedTheme.name)}
        >
          <LightIcon
            className={clsxm(
              '[[data-theme=light]_&]:block hidden h-4 w-4',
              iconFillClass,
            )}
          />
          <DarkIcon
            className={clsxm(
              '[[data-theme=dark]_&]:block hidden h-4 w-4',
              iconFillClass,
            )}
          />
          <LightIcon
            className={clsxm(
              '[:not(.dark)[data-theme=system]_&]:block hidden h-4 w-4',
              variant === 'light' ? 'fill-white' : 'fill-slate-400',
            )}
          />
          <DarkIcon
            className={clsxm(
              '[.dark[data-theme=system]_&]:block hidden h-4 w-4',
              variant === 'light' ? 'fill-white' : 'fill-slate-400',
            )}
          />
        </Listbox.Button>
      </SimpleTooltip>
      <Listbox.Options
        className={clsxm(
          'absolute z-50 top-full mt-3 w-36 space-y-1 rounded-xl bg-white p-3 text-sm font-medium shadow-md shadow-black/5 ring-1 ring-black/5 dark:bg-slate-800 dark:ring-white/5',
          align === 'center' && 'left-1/2 -translate-x-1/2',
          align === 'left' && 'left-0',
          align === 'right' && 'right-0',
        )}
      >
        {themes.map((theme) => (
          <Listbox.Option
            key={theme.value}
            value={theme}
            className={({ active, selected }) =>
              clsxm(
                'flex cursor-pointer select-none items-center rounded-[0.625rem] p-1',
                {
                  'text-primary-500': selected,
                  'text-slate-900 dark:text-white': active && !selected,
                  'text-slate-700 dark:text-slate-400': !active && !selected,
                  'bg-slate-100 dark:bg-slate-900/40': active,
                },
              )
            }
          >
            {({ selected }) => (
              <>
                <div className='rounded-md bg-white p-1 shadow ring-1 ring-slate-900/5 dark:bg-slate-700 dark:ring-inset dark:ring-white/5'>
                  <theme.icon
                    className={clsxm(
                      'h-4 w-4',
                      selected
                        ? 'fill-primary-400 dark:fill-primary-400'
                        : 'fill-slate-400',
                    )}
                  />
                </div>
                <div className='ml-3'>{t(theme.name)}</div>
              </>
            )}
          </Listbox.Option>
        ))}
      </Listbox.Options>
    </Listbox>
  );
}
